




















































































































































































import {Component, Mixins} from 'vue-mixin-decorator';
import {getModule} from 'vuex-module-decorators';
import {Prop} from 'vue-property-decorator';
import {Inject} from 'inversify-props';

import {Notify} from '@/utils/Notify';
import {IpTools} from '@/utils/IpTools';
import {Time} from '@/utils/Time';

import {Profile} from '@/model/network/profile/Profile';
import {Connection} from '@/model/network/connection/Connection';
import {
  ConnectionState,
  ConnectionStateColor,
  ConnectionStateLabel,
} from '@/model/network/connection/ConnectionState';
import {Event} from '@/model/Event';
import {Level} from '@/model/Notification';
import {ConnectionStateReason, ConnectionStateReasonLabel} from '@/model/network/connection/ConnectionStateReason';

import {ApiStore} from '@/store/Api.store';
import NetworkProfilesStore from '@/store/network/NetworkProfiles.store';
import NetworkConnectionsStore from '@/store/network/NetworkConnections.store';

import {IEventsService} from '@/services/IEvents.service';
import {INetworkProfileService} from '@/services/network/INetworkProfileService';
import {INetworkConnectionService} from '@/services/network/INetworkConnectionService';
import {ErrorsBag, SafeOperation} from '@/services/SafeOperation';
import {IApiService} from '@/services/IApi.service';
import {INetworkNetworkingService} from '@/services/network/INetworkNetworkingService';

import {BaseComponent} from '@/mixins/BaseComponent';
import NetworkVpnSettingsDisplay from '@/components/Networking/Vpn/NetworkVpnSettingsDisplay.vue';
import NetworkVpnSettingsDisplayOpenVpn from '@/components/Networking/Vpn/NetworkVpnSettingsDisplayOpenVpn.vue';
import NetworkConnectionSettingsDisplay from '@/components/Networking/Profile/NetworkConnectionSettingsDisplay.vue';
import NetworkVpnSettingsEditorModal from '@/components/Networking/Vpn/NetworkVpnSettingsEditorModal.vue';
import NetworkConnectionStatus from '@/components/Networking/Connection/NetworkConnectionStatus.vue';
import SafeUpdateDialog from '@/components/Networking/SafeUpdate/SafeUpdateDialog.vue';
import SafeUpdateSettings from '@/components/Networking/SafeUpdate/SafeUpdateSettings.vue';
import PropertyDisplayer from '@/components/Utils/PropertyDisplayer.vue';

@Component({
  components: {
    SafeUpdateSettings,
    SafeUpdateDialog,
    PropertyDisplayer,
    NetworkConnectionStatus,
    NetworkVpnSettingsEditorModal,
    NetworkConnectionSettingsDisplay,
    NetworkVpnSettingsDisplayOpenVpn,
    NetworkVpnSettingsDisplay,
  },
})
export default class NetworkVpn extends Mixins<BaseComponent>(BaseComponent) {

  private ConnectionState = ConnectionState;
  private ConnectionStateLabel = ConnectionStateLabel;
  private ConnectionStateColor = ConnectionStateColor;
  private IpTools = IpTools;

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  @Inject(Symbol.for('INetworkNetworkingService'))
  private networkingService!: INetworkNetworkingService;

  @Inject(Symbol.for('IEventsService'))
  private eventsService!: IEventsService;

  @Inject(Symbol.for('INetworkProfileService'))
  private profileService!: INetworkProfileService;

  @Inject(Symbol.for('INetworkConnectionService'))
  private connectionService!: INetworkConnectionService;

  private apiStore: ApiStore = getModule(ApiStore, this.$store);

  private profilesStore: NetworkProfilesStore = getModule(NetworkProfilesStore, this.$store);

  private connectionsStore: NetworkConnectionsStore = getModule(NetworkConnectionsStore, this.$store);

  @Prop(Object)
  private profile!: Profile;

  private settingsEditor: boolean = false;

  private showDeleteConfirmation: boolean = false;

  private connecting: boolean = false;
  private disconnecting: boolean = false;
  private deleting: boolean = false;

  private stateSubscriptionId: number | null = null;

  private safe: boolean = true;

  private host: string = '';

  private rollbackOccurred: boolean = false;

  private get connection(): Connection | null {
    return this.connectionsStore.Connections?.find((c) => c.UUID === this.profile.Settings.Connection.Uuid) ?? null;
  }

  private get connectionState(): ConnectionState {
    return this.connection?.State ?? ConnectionState.Deactivated;
  }

  private async mounted() {
    await this.refresh();
  }

  private async refresh() {
    this.setLoading();
    try {
      try {
        await this.profilesStore.fetchProfile(this.profile.Id);
      } catch (error) {
        Notify.Error(this.logger, `Could no get profile '${this.profile.Name}'`, error);
      }

      try {
        await this.connectionsStore.fetchConnection(this.profile.Id);
      } catch (error) {
        if (error.code === 404) {
          const connection = this.connectionsStore.Connections?.find((c) => c.UUID === this.profile.Settings.Connection.Uuid);
          if (connection) {
            this.connectionsStore.removeConnection(connection);
          }
        } else {
          Notify.Error(this.logger, `Could not get VPN connection details`, error);
        }
      }

      if (this.connection === null || (this.connection.State === ConnectionState.Activated || this.connection.State === ConnectionState.Deactivated)) {
        this.connecting = false;
        this.disconnecting = false;
      }
    } finally {
      this.setLoaded();
    }
  }

  private async connect(safe: boolean) {
    this.logger.trace('Connecting');
    this.connecting = true;

    try {

      const connectProcedure = async (): Promise<Connection> => {
        const connection = await this.profileService.connect(this.profile.Id);
        try {
          await this.subscribeState(connection!);
        } catch (error) {
          Notify.Error(this.logger, `Could not subscribe to connection state changes, please refresh manually`, error);
        }
        this.connectionsStore.setConnection(connection!);

        return connection;
      };

      if (safe) {
        this.rollbackOccurred = false;
        const safeOperation = new SafeOperation(this.networkingService, this.apiService);

        const rollbackDelay = 30;
        await safeOperation.Execute(
            async () => {
              try {
                const connection = await connectProcedure();

                let i = rollbackDelay - 1;
                while (connection?.State === ConnectionState.Activating && i-- > 0) {
                  await Time.sleep(1000);
                }
              } catch (error) {
                throw new ErrorsBag(new Error(`Could not connect '${this.profile.Id}'`), [error]);
              }
            },
            undefined,
            (level: Level, message: string, details?: string[], error?: Error) => {
              Notify.Notify(this.logger, level, message, details, error);
            },
            this.host,
            rollbackDelay,
            undefined,
            async () => {
              this.logger.trace('Rollback procedure');

              this.rollbackOccurred = true;

              if (this.stateSubscriptionId !== null) {
                try {
                  await this.eventsService.unsubscribe(this.stateSubscriptionId);
                } catch {

                }
              }

              if (this.connection) {
                /* If a rollback occurs, the tunnel is removed, restoring connection. But the VPN is still connected */
                await this.connectionService.disconnect(this.connection!.Id);
                this.connectionsStore.removeConnection(this.connection!);
              }
            },
        );
      } else {
        await connectProcedure();
      }

    } catch (error) {
      Notify.Error(this.logger, error.message, error, error.details);
      this.connecting = false;
      return;
    }
  }

  private async disconnect(safe: boolean) {

    if (!this.connection) {
      throw new Error('no connection, cannot disconnect');
    }

    this.logger.trace('Disconnecting');
    this.disconnecting = true;

    try {

      const disconnectProcedure = async () => {

        try {
          await this.removeCarrierSecondaries();
        } catch (error) {
          Notify.Warn(this.logger, 'Could not disable auto-connect', error);
        }

        await this.connectionService.disconnect(this.connection!.Id);
        this.connectionsStore.removeConnection(this.connection!);
      };

      if (safe) {
        const safeOperation = new SafeOperation(this.networkingService, this.apiService);

        await safeOperation.Execute(
            disconnectProcedure,
            undefined,
            (level: Level, message: string, details?: string[], error?: Error) => {
              Notify.Notify(this.logger, level, message, details, error);
            },
        );
      } else {
        await disconnectProcedure();
      }

    } catch (error) {
      Notify.Error(this.logger, `Could not disconnect VPN`, error);
    }

    this.disconnecting = false;
  }

  private async deleteProfile() {
    this.deleting = true;
    try {
      await this.profileService.delete(this.profile.Id);
      this.profilesStore.removeProfile(this.profile);
    } catch (error) {
      Notify.Error(this.logger, `Could not delete VPN`, error);
    }
    this.deleting = false;
  }

  private async subscribeState(connection: Connection, subscribeBackend: boolean = false) {
    this.stateSubscriptionId = await this.eventsService.subscribe('network.connection.state', async (event: Event) => {

      if (event.Data.connection !== connection.Id) {
        return;
      }

      this.logger.trace('State changed', event.Data);

      const state = event.Data.state as ConnectionState;
      this.connectionsStore.setState({connection, state});

      switch (state) {
        case ConnectionState.Activated:
          await this.onConnected(connection);
          break;

        case ConnectionState.Deactivated:
          await this.onDisconnected(connection, event.Data.reason);
          break;
      }
    });

    if (subscribeBackend) {
      await this.connectionService.subscribeState(connection.Id);
    }
  }

  private async unsubscribeState(connection: Connection) {
    if (this.stateSubscriptionId !== null) {
      await this.eventsService.unsubscribe(this.stateSubscriptionId);
      await this.connectionService.unsubscribeState(connection.Id);
    }
  }

  private async onConnected(connection: Connection) {
    if (this.connecting && !this.rollbackOccurred) {
      this.connecting = false;
      await this.unsubscribeState(connection);

      try {
        await this.addCarrierSecondaries();
      } catch (error) {
        Notify.Error(this.logger, 'Could not enable auto-connect, VPN will be disabled on next disconnection', error);
      }

      try {
        await this.connectionsStore.fetchConnection(connection.Id);
      } catch (error) {
        Notify.Warn(this.logger, 'Could get the details of the VPN connection', error);
      }
    }
  }

  private async onDisconnected(connection: Connection, reason: ConnectionStateReason) {
    if (this.connecting) {
      this.connecting = false;
      Notify.Error(this.logger, `Could not connect '${connection.Id}'`, null, [ConnectionStateReasonLabel.get(reason) ?? reason, 'Please check the logs for details']);
    }
    this.connectionsStore.removeConnection(connection);
  }

  private async addCarrierSecondaries() {

    const carrierProfile = await this.getCarrierProfile();
    const carrierProfileUpdate = new Profile(carrierProfile);

    if (!carrierProfileUpdate.Settings.Connection.Secondaries) {
      carrierProfileUpdate.Settings.Connection.Secondaries = [];
    }

    carrierProfileUpdate.Settings.Connection.Secondaries.push(this.profile.Settings.Connection.Uuid);

    this.logger.debug(`Adding VPN to carrier profile secondaries`);
    const updatedProfile = await this.profileService.update(carrierProfileUpdate.Id, carrierProfileUpdate);
    this.profilesStore.setProfile(updatedProfile);
  }

  private async removeCarrierSecondaries() {

    const carrierProfile = await this.getCarrierProfile();
    const carrierProfileUpdate = new Profile(carrierProfile);

    if (carrierProfileUpdate.Settings.Connection.Secondaries) {
      carrierProfileUpdate.Settings.Connection.Secondaries = carrierProfileUpdate.Settings.Connection.Secondaries.filter((uuid) => uuid !== this.profile.Settings.Connection.Uuid);
    }

    this.logger.debug(`Removing VPN to carrier profile secondaries`);
    const updatedProfile = await this.profileService.update(carrierProfileUpdate.Id, carrierProfileUpdate);
    this.profilesStore.setProfile(updatedProfile);
  }

  private async getCarrierProfile(): Promise<Profile> {

    // TODO: getting all details is a bit overkill, we should just fetch devices (#GraphQL)
    await this.connectionsStore.fetchConnections(true);

    const primaryConnection = this.connectionsStore.PrimaryConnection;
    if (!primaryConnection) {
      throw new Error('Could not get primary connection');
    }

    const carrierProfile = this.profilesStore.Profiles?.find((p: Profile) => p.Settings.Connection.Uuid === primaryConnection?.UUID);
    if (!carrierProfile) {
      throw new Error('Could not get carrier profile');
    }

    return carrierProfile;
  }
}
